import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompletedActions } from "@/hooks/useCompletedActions";
import { ActionPlanTab } from "@/components/report/ActionPlanTab";
import type { ActionItem, AnalysisResult } from "@/types/analysis";
import { Loader2, ClipboardList, ChevronDown, Search } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface AnalysisGroup {
  id: string;
  url: string;
  date: string;
  actions: ActionItem[];
  pageContent?: string;
}

type SortKey = "recent" | "most-actions" | "least-complete";

const CATEGORY_LABEL: Record<ActionItem["category"], string> = {
  seo: "SEO",
  "ai-visibility": "AI Visibility",
  authority: "Authority",
  technical: "Technical",
};

const formatUrl = (raw: string) => {
  try {
    const u = new URL(raw);
    const path = u.pathname === "/" ? "" : u.pathname;
    return `${u.hostname}${path}`;
  } catch {
    return raw;
  }
};

export default function ActionPlanPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<AnalysisGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("recent");
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  usePageMeta({ title: "Action Plan — Unranked" });
  const completedActions = useCompletedActions();

  useEffect(() => {
    if (!user) return;

    const fetch = async () => {
      const { data } = await supabase
        .from("analyses")
        .select("id, url, results, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (data) {
        const mapped: AnalysisGroup[] = data
          .map((row) => {
            const results = row.results as unknown as AnalysisResult;
            return {
              id: row.id,
              url: row.url,
              date: row.created_at,
              actions: results?.actionPlan ?? [],
              pageContent: results?.pageContent,
            };
          })
          .filter((g) => g.actions.length > 0);

        setGroups(mapped);
        // Auto-open if only one analysis
        if (mapped.length === 1) {
          setOpenIds(new Set([mapped[0].id]));
        }
      }
      setLoading(false);
    };

    fetch();
  }, [user]);

  const toggleOpen = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOpen = groups.length > 0 && openIds.size === groups.length;
  const toggleAll = () => {
    if (allOpen) setOpenIds(new Set());
    else setOpenIds(new Set(groups.map((g) => g.id)));
  };

  const filteredSortedGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? groups.filter((g) => g.url.toLowerCase().includes(q))
      : groups;

    const withStats = filtered.map((g) => {
      const total = g.actions.length;
      const done = g.actions.filter((a) =>
        completedActions.completedIds.has(a.id)
      ).length;
      return { group: g, total, done, percent: total === 0 ? 0 : (done / total) * 100 };
    });

    const sorted = [...withStats].sort((a, b) => {
      if (sortBy === "recent") {
        return new Date(b.group.date).getTime() - new Date(a.group.date).getTime();
      }
      if (sortBy === "most-actions") {
        return b.total - a.total;
      }
      // least-complete
      return a.percent - b.percent;
    });

    return sorted;
  }, [groups, search, sortBy, completedActions.completedIds]);

  if (loading || completedActions.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalActions = groups.reduce((sum, g) => sum + g.actions.length, 0);

  if (totalActions === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">No action items yet</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Run an analysis to generate your action plan.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Action Plan</h1>
        <p className="text-sm text-muted-foreground mt-1">
          All action items across your analyses in one place.
        </p>
      </div>

      {groups.length > 1 && (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by URL..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortKey)}>
            <SelectTrigger className="sm:w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most recent</SelectItem>
              <SelectItem value="most-actions">Most actions</SelectItem>
              <SelectItem value="least-complete">Least complete</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={toggleAll} className="sm:w-auto">
            {allOpen ? "Collapse all" : "Expand all"}
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {filteredSortedGroups.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No analyses match your search.
          </p>
        )}
        {filteredSortedGroups.map(({ group, total, done, percent }) => {
          const isOpen = openIds.has(group.id);
          const categories = Array.from(
            new Set(group.actions.map((a) => a.category))
          );

          return (
            <Card key={group.id} className="overflow-hidden">
              <Collapsible open={isOpen} onOpenChange={() => toggleOpen(group.id)}>
                <CollapsibleTrigger className="w-full text-left p-4 sm:p-5 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <ChevronDown
                      className={cn(
                        "w-5 h-5 text-muted-foreground shrink-0 mt-0.5 transition-transform",
                        isOpen && "rotate-180"
                      )}
                    />
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <h2 className="text-sm sm:text-base font-semibold truncate">
                          {formatUrl(group.url)}
                        </h2>
                        <div className="flex flex-wrap gap-1">
                          {categories.map((c) => (
                            <Badge key={c} variant="secondary" className="text-[10px]">
                              {CATEGORY_LABEL[c]}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(group.date).toLocaleDateString()} · {total}{" "}
                        action{total === 1 ? "" : "s"} · {done} done
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={percent} className="h-1.5 flex-1" />
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                          {Math.round(percent)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t px-4 sm:px-5 py-4">
                    <ActionPlanTab
                      data={group.actions}
                      completedActions={completedActions}
                      analysisId={group.id}
                      pageUrl={group.url}
                      pageContent={group.pageContent}
                      onActionUpdated={(updated) => {
                        setGroups((prev) =>
                          prev.map((g) =>
                            g.id === group.id
                              ? {
                                  ...g,
                                  actions: g.actions.map((a) =>
                                    a.id === updated.id ? updated : a
                                  ),
                                }
                              : g
                          )
                        );
                      }}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
